// Anuncios de Meta desde el Chat — ADR-2026-09-24-anuncios-de-meta-en-el-chat.
//
// A diferencia de Instagram, acá no hay herramientas del front: el backend le suma al
// turno el MCP OFICIAL de Meta (`mcp.facebook.com/ads`) con la llave de la marca, en
// modo sólo lectura (atlas-backend, `services/agent/metaAds.ts`). El front hace tres
// cosas: pedirlo (`meta_ads` + `brand_id` en el cuerpo), contarle al agente qué cuenta
// está conectada, y la tarjeta para ir a conectarla.
//
// Nada de React ni de red acá adentro: cada regla se prueba sola.

import type { HerramientaChat } from './tipos';
import type { EstadoDeAnuncios } from '../lib/metaAdsConexion';
import type { Intelligence } from '../pages/v2/agentModels';

export const NOMBRE_CONECTAR_ANUNCIOS = 'conectar_anuncios';

/**
 * Las herramientas de LECTURA que el backend le deja usar al agente. Espejo de
 * `META_ADS_HERRAMIENTAS_DE_LECTURA` en atlas-backend: si allá cambia, cambia acá (lo
 * usan el test de las skills y la etiqueta de actividad).
 */
export const HERRAMIENTAS_DE_ANUNCIOS_DEL_SERVIDOR = [
  'ads_get_ad_entities',
  'ads_get_field_context',
  'ads_get_opportunity_score',
  'ads_insights_advertiser_context',
  'ads_insights_anomaly_signal',
  'ads_insights_auction_ranking_benchmarks',
  'ads_insights_industry_benchmark',
  'ads_insights_performance_trend',
  'ads_get_errors',
  'ads_get_help_article',
  'ads_account_get_activity_logs',
  'ads_get_creatives',
  'ads_get_creative_ads',
  'ads_get_ad_images',
  'ads_get_ad_videos',
  'ads_get_ad_preview',
  'ads_library_search',
  'ads_experiment_list_tests',
] as const;

/**
 * La tarjeta con el botón a Mi marca. Viaja SIEMPRE para quien puede conectar anuncios
 * (conectada o no): las herramientas de Meta las suma el backend, y si la llave venció a
 * mitad de charla el agente se da cuenta porque no las tiene. Con esta a mano puede
 * mandarlo a reconectar en vez de quedarse sin salida.
 */
export const HERRAMIENTA_CONECTAR_ANUNCIOS: HerramientaChat = {
  name: NOMBRE_CONECTAR_ANUNCIOS,
  description:
    'Muestra en el chat una tarjeta con el botón para ir a conectar sus anuncios de Meta (Facebook e Instagram Ads) en Mi marca. Usala cuando pregunte por SUS anuncios o campañas y vos NO tengas las herramientas `ads_…` (no los conectó, o se le venció la conexión). No la uses si estás en inteligencia Baja: ahí lo que tiene que hacer es pasar a Media. No lo lleva a ningún lado: el botón lo toca él. Contale en una línea qué vas a poder ver (gasto, resultados, qué campañas y piezas rinden; sin tocar ni gastar nada en su cuenta) y mostrá la tarjeta en la misma respuesta.',
  input_schema: { type: 'object', properties: {} },
};

const TITULO = '══════════ ANUNCIOS DE META ══════════';

/**
 * La línea que viaja en el contexto de cada turno. Sale de nuestra base (la edge
 * `ads-conexion`), no de Meta. null = no se dice nada: sin conexión, o no se pudo averiguar.
 */
export function bloqueDeAnuncios(
  estado: EstadoDeAnuncios | null,
  opciones: {
    inteligencia: Intelligence;
    /** Estuvo conectada en esta sesión y ya no: si vio números, son viejos. */
    seDesconecto?: boolean;
  },
): string | null {
  if (!estado || estado.error) return null;
  const cuenta = estado.cuenta?.nombre ? `"${estado.cuenta.nombre}"` : 'su cuenta publicitaria';

  if (!estado.conectada && opciones.seDesconecto && estado.estado !== 'vencida') {
    return `${TITULO}\nLa marca YA NO tiene sus anuncios conectados: se desconectaron durante esta charla. Si antes viste números, eran de cuando estaba conectada: no digas que sigue conectada. Si quiere verlos de nuevo, mostrale la tarjeta con \`${NOMBRE_CONECTAR_ANUNCIOS}\`.`;
  }
  if (!estado.conectada && estado.estado === 'vencida') {
    return `${TITULO}\nLa marca tenía conectada ${cuenta}, pero la conexión venció (dura unos 60 días). Si pregunta por sus anuncios, mostrale la tarjeta para reconectarla con \`${NOMBRE_CONECTAR_ANUNCIOS}\`. No inventes números.`;
  }
  if (!estado.conectada || !estado.cuenta) return null;

  if (opciones.inteligencia === 'baja') {
    return `${TITULO}\nLa marca tiene conectada ${cuenta}, pero en inteligencia Baja no podés leer sus anuncios. Si pregunta por sus anuncios o campañas, decile en una línea que cambie la inteligencia a Media (en el selector de abajo del chat) y te vuelva a preguntar. No inventes números ni muestres la tarjeta de conectar: ya está conectada.`;
  }

  return `${TITULO}\nLa marca tiene conectada su cuenta publicitaria de Meta ${cuenta} (ad_account_id ${estado.cuenta.id}). Para SUS anuncios tenés las herramientas \`ads_…\` del MCP oficial de Meta: sólo leen, y no cuestan créditos aparte. Trabajá SIEMPRE sobre esa cuenta (su llave puede ver otras: no las uses). Antes de contar números cargá la skill \`anuncios\`. No podés crear, pausar, editar ni activar nada: si te lo pide, explicale que eso se hace en el Administrador de anuncios. Si no ves las herramientas \`ads_…\`, la conexión se cayó: mostrale la tarjeta con \`${NOMBRE_CONECTAR_ANUNCIOS}\`. No lo ofrezcas en cada respuesta: sólo cuando venga al caso.`;
}

/**
 * Lo que se suma al cuerpo del pedido: el backend busca la conexión de ESA marca con el
 * tenant de la sesión. En Baja no se pide (Gemini no tiene MCP).
 */
export function cuerpoDeAnuncios(
  estado: EstadoDeAnuncios | null,
  marcaId: string | null,
  inteligencia: Intelligence,
): { meta_ads: true; brand_id: string } | Record<string, never> {
  if (!estado?.conectada || !marcaId || inteligencia === 'baja') return {};
  return { meta_ads: true, brand_id: marcaId };
}

